import { Box, Button, Typography } from "@mui/joy";
import Add from "@mui/icons-material/Add";
import React, { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import ClientAdd from "./Client-Add";
import GenericTableView from "../../components/GenericTableView";
import { FilterMatchMode } from "primereact/api";
import { GenericTableColumTypes, GenericTableFilterTypes } from "../../enum/TableView";
import { MenuItem } from "primereact/menuitem";
import { GenericTableColumns } from "../../models/GenericTableViewModel";
import { removeClient, updateClient } from "../../service/clientService";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { RouteComponentProps } from "../../models/RouteComponent";
import { UserTypes } from "../../enum/UserTypes";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { saveActiveClient, saveActiveSite, saveActiveType, saveLogoS3Key } from "../../service/session-service";

export default function ClientTable(props: RouteComponentProps) {
  const navigate = useNavigate();

  const [selectedRecord, setSelectedRecord] = useState({} as any);
  const [visible, setVisible] = useState(false);
  const [reload, setReload] = useState(false);

  const toast = useRef(null as any);

  const consultant_id = props?.session?.consultant_id;

  let url = "client";

  if (!!consultant_id) {
    url = url.concat(`?consultantId=${consultant_id}`);
  }

  const baseFilter = {
    subscription: { value: true, matchMode: FilterMatchMode.CONTAINS },
  };

  const deleteButton = (row: any) => {
    console.log("deleteButton row == ", selectedRecord);
    removeClient(selectedRecord.id);
  };

  const addButton = (row: any) => {
    // console.log("addButton row == ", row)
    setSelectedRecord(null);
    setVisible(true);
  };

  const itemSelected = (row: any) => {
    let value = row.value;
    setSelectedRecord(value);
    setTimeout(() => {
      viewSelected(value);
    }, 50);
  };

  const viewSelected = (value: any) => {
    const clientId = value.id ?? selectedRecord.id;
    const logoS3Key = selectedRecord?.s3_key || `client/${value.id}`;
    
    // Save active client to localStorage
    saveActiveClient(clientId, value.name);
    saveActiveSite(undefined, undefined); // Clear site when changing client
    saveActiveType(UserTypes.Admin);
    saveLogoS3Key(logoS3Key);
    
    props.setSession({
      ...props.session,
      ...{
        client_id: clientId,
        active_type: UserTypes.Admin,
        logo_s3_key: logoS3Key,
      },
    });
    let breadcrumbs = props.breadcrumbs;
    breadcrumbs.push(
      {
        label: value.name,
        link: "/client-table",
        userType: UserTypes.DSF,
        isLabel: true,
      },
      {
        label: "Dashboard",
        link: "/client-dashboard",
        userType: UserTypes.Client,
      }
    );

    props.setBreadcrumbs(breadcrumbs);
    navigate("/client-dashboard");
  };

  const contextItemSelected = (row: any) => {
    setSelectedRecord(row.value);
  };

  const acceptFunc = async () => {
    props.setGlobalLoader(true);
    const client = await removeClient(selectedRecord.id);
    props.setGlobalLoader(false);
    toast.current.show({ severity: "success", summary: "Client Deleted Successfully", life: 3000 });
    setReload(true);
  };

  const deleteConfirm = (row: any) => {
    confirmDialog({
      message: "Are you sure you want to proceed?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => acceptFunc(),
    });
  };

  const changeStatusFunc = async (status: boolean) => {
    props.setGlobalLoader(true);
    const client = await updateClient({ subscription: status }, selectedRecord.id);
    props.setGlobalLoader(false);
    setReload(!reload);
    toast.current.show({
      severity: "success",
      summary: status ? "Client Activated Successfully" : "Client Deactivated Successfully",
      life: 3000,
    });
  };

  const disableConfirm = (row: any) => {
    confirmDialog({
      message: "Are you sure you want to deactivate client?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => changeStatusFunc(false),
    });
  };

  const activateConfirm = (row: any) => {
    confirmDialog({
      message: "Are you sure you want to activate client?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => changeStatusFunc(true),
    });
  };

  const DialogSetVisible = (row: any) => {
    setVisible(true);
  };

  const contextItems = () => {
    let objArray = [
      { label: "View", icon: "pi pi-fw pi-eye", command: viewSelected },
      { label: "Edit", icon: "pi pi-fw pi-pencil", command: DialogSetVisible },
    ];
    if (selectedRecord?.subscription) {
      objArray.push({
        label: "Deactivate",
        icon: "pi pi-fw pi-times-circle",
        command: disableConfirm,
      });
    } else {
      objArray.push({
        label: "Activate",
        icon: "pi pi-fw pi-check-circle",
        command: activateConfirm,
      });
    }
    objArray.push({ label: "Delete", icon: "pi pi-fw pi-trash", command: deleteConfirm });
    return objArray;
  };

  const columns: GenericTableColumns[] = [
    {
      field: "name",
      header: "Name",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: true,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "industry_list",
      header: "Industry",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: true,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Select,
        placeholder: "Select an Industry",
        options: [
          {
            label: "Construction",
            value: "Construction",
          },
          {
            label: "Mining",
            value: "Mining",
          },
          {
            label: "Engineering",
            value: "Engineering",
          },
          {
            label: "Automotive",
            value: "Automotive",
          },
          {
            label: "Estates",
            value: "Estates",
          },
        ],
      },
    },
    {
      field: "",
      header: "Client",
      type: GenericTableColumTypes.Contact,
      visible: true,
      sort: false,
      filter: false,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
      contactDetails: {
        cellField: "mobile",
        emailField: "email",
        labelField: "consultant_name",
        avatarSrc: "photo",
      },
    },
    {
      field: "consultant_name",
      header: "Consultant",
      type: GenericTableColumTypes.Text,
      visible: !consultant_id,
      sort: true,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "subscription",
      header: "Subscription",
      type: GenericTableColumTypes.Boolean,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Boolean,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "compliance_status",
      header: "Compliance Status",
      type: GenericTableColumTypes.Status,
      visible: true,
      sort: false,
      filter: false,
      filterOption: {
        type: GenericTableFilterTypes.Status,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "contact_person",
      header: "ContactDetails Person",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: false,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "email",
      header: "ContactDetails Details",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: false,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
  ];

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Clients
        </Typography>

        {/* <Button
            color="primary"
            startDecorator={<Add />}
            size="sm"
            onClick={() => setVisible(true)}
          >Add</Button> */}
      </Box>

      <GenericTableView
        reload={reload}
        baseFilters={baseFilter}
        url={url}
        columns={columns}
        contextItems={contextItems}
        addButton={addButton}
        itemSelect={itemSelected}
        contextItemSelected={contextItemSelected}
      />

      <Dialog
        header={selectedRecord?.id ? "Update Client" : "Add Client"}
        visible={visible}
        style={{ width: "50vw" }}
        onHide={() => setVisible(false)}
      >
        <ClientAdd
          setGlobalLoading={props.setGlobalLoader}
          setReload={setReload}
          uuid={uuid()}
          reload={reload}
          record={selectedRecord}
          setVisible={setVisible}
          toast={toast}
          session={props.session}
        />
      </Dialog>

      <ConfirmDialog />

      <Toast ref={toast} />
    </React.Fragment>
  );
}
